import { useState } from "react";
import { Navigate } from "react-router-dom";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { Btn, Surface, Brand } from "@championcare/ui";
import { COGNITO_ENABLED, STAFF_DOMAINS } from "../config";
import { useAppAuth } from "../auth";

// Cognito sign-in (custom form). Operator accounts are admin-created, so beyond credentials this
// handles TOTP step-up (CONFIRM_SIGN_IN_WITH_TOTP_CODE) and the forced password change on first
// sign-in of an admin-created account (CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED). On success the
// bridge's Hub listener flips the context; the /v1/command/me probe then authorizes.
function CognitoSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [step, setStep] = useState<"credentials" | "totp" | "newPassword">("credentials");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function routeNextStep(signInStep: string) {
    if (signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      setChallenge("");
      setStep("totp");
    } else if (signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setChallenge("");
      setStep("newPassword");
    } else if (signInStep !== "DONE") {
      setError("This account requires a sign-in step this console doesn’t support. Contact your administrator.");
      setStep("credentials");
    }
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await signIn({ username: email.trim(), password });
      routeNextStep(nextStep.signInStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitChallenge(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: challenge });
      routeNextStep(nextStep.signInStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn’t work. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const fieldStyle = { display: "grid", gap: 6, marginBottom: 12 } as const;
  const inputStyle = {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--line-2, rgba(22,22,22,0.14))",
    background: "var(--paper, #fff)",
    color: "inherit",
    font: "inherit",
  } as const;

  if (step === "totp" || step === "newPassword") {
    const isTotp = step === "totp";
    return (
      <Surface className="signin__card">
        <h2 className="signin__heading">{isTotp ? "Two-factor check" : "Set a new password"}</h2>
        <p className="signin__hint">
          {isTotp
            ? "Enter the 6-digit code from your authenticator app."
            : "Your account was created by an administrator — choose your own password to continue."}
        </p>
        <form onSubmit={submitChallenge}>
          <div style={fieldStyle}>
            <label htmlFor="challenge">{isTotp ? "Authenticator code" : "New password"}</label>
            <input
              id="challenge"
              style={inputStyle}
              type={isTotp ? "text" : "password"}
              inputMode={isTotp ? "numeric" : undefined}
              autoComplete={isTotp ? "one-time-code" : "new-password"}
              maxLength={isTotp ? 6 : undefined}
              minLength={isTotp ? undefined : 12}
              placeholder={isTotp ? "123456" : "12+ characters"}
              value={challenge}
              onChange={(e) => setChallenge(isTotp ? e.target.value.replace(/\D/g, "") : e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="signin__hint" role="alert" style={{ color: "var(--red, #dc2626)" }}>{error}</p>
          )}
          <Btn as="button" variant="primary" disabled={busy || (isTotp && challenge.length < 6)}>
            {busy ? "Verifying…" : isTotp ? "Verify" : "Set password & continue"}
          </Btn>
        </form>
      </Surface>
    );
  }

  return (
    <Surface className="signin__card">
      <h2 className="signin__heading">Operator sign in</h2>
      <p className="signin__hint signin__domain-hint">
        Use your {STAFF_DOMAINS.join(" or ")} account.
      </p>
      <form onSubmit={submitCredentials}>
        <div style={fieldStyle}>
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            style={inputStyle}
            type="email"
            autoComplete="email"
            placeholder="you@forcebase.ai"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div style={fieldStyle}>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            style={inputStyle}
            type="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        {error && (
          <p className="signin__hint" role="alert" style={{ color: "var(--red, #dc2626)" }}>{error}</p>
        )}
        <Btn as="button" variant="primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Btn>
      </form>
    </Surface>
  );
}

export function SignIn() {
  const { isLoaded, isSignedIn, devSignIn } = useAppAuth();
  const [busy, setBusy] = useState(false);

  if (isLoaded && isSignedIn) return <Navigate to="/" replace />;

  return (
    <div className="signin">
      <div className="signin__masthead">
        <Brand className="signin__brand" size={30} />
        <p className="signin__tag">Command — internal operations</p>
      </div>
      {COGNITO_ENABLED ? (
        <CognitoSignIn />
      ) : (
        <Surface className="signin__card">
          <h2 className="signin__heading">Operator sign in</h2>
          <p className="signin__hint">
            Cognito (with MFA) isn&rsquo;t configured here. Continue with a local dev session to
            explore the back-office.
          </p>
          <Btn
            as="button"
            variant="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await devSignIn?.();
              setBusy(false);
            }}
          >
            {busy ? "Signing in…" : "Continue (dev session)"}
          </Btn>
        </Surface>
      )}
    </div>
  );
}
